import API from 'services/api';
import getHeaders from 'services/http-header';
import {
    IIntegrationPartnerCreateRequest,
    IIntegrationPartnerGroupUpdateRequest,
    IPlatformSetting,
} from '@core/models/integration.model';

/** Multipart upload must not use fetchWithRetry (body is read once) or forced JSON Content-Type. */
function postMultipart(url: string, form: FormData) {
    return API.post(url, form, {
        transformRequest: [
            (data, headers) => {
                if (data instanceof FormData) {
                    const h = { ...getHeaders(), ...headers };
                    delete h['Content-Type'];
                    delete h['content-type'];
                    return data;
                }
                return data;
            },
        ],
    });
}

function certFileForm(file: File) {
    const form = new FormData();
    form.append('file', file, file.name);
    return form;
}

class IntegrationsService {
    private readonly base = '/admin/integrations';

    getStatus() {
        return API.get(`${this.base}/status`);
    }

    listTemplates() {
        return API.get(`${this.base}/templates`);
    }

    listTemplatesForWizard() {
        return API.get(`${this.base}/templates`, { params: { forWizard: true } });
    }

    getPlatformSettings() {
        return API.get(`${this.base}/platform-settings`);
    }

    putPlatformSettings(settings: IPlatformSetting[]) {
        return API.put(`${this.base}/platform-settings`, settings);
    }

    validateEpcisIdentity(gln?: string, sgln?: string, companyPrefix?: string) {
        return API.get(`${this.base}/epcis-identity/validate`, {
            params: { gln, sgln, companyPrefix },
        });
    }

    getEpcisDataHealth() {
        return API.get(`${this.base}/epcis-identity/data-health`);
    }

    saveEpcisIdentity(gln: string, sgln: string, companyPrefix?: string) {
        return API.put(`${this.base}/epcis-identity`, null, {
            params: { gln, sgln, companyPrefix },
        });
    }

    previewEpcisProductIdentity(productId: number, sgtinItemKey: string) {
        return API.post(`${this.base}/epcis-product-identity/preview`, null, {
            params: { productId, sgtinItemKey },
        });
    }

    getToteEpcisReadiness(toteId: number) {
        return API.get(`${this.base}/totes/${toteId}/epcis-readiness`);
    }

    getToteShipmentEpcisStatus(toteId: number) {
        return API.get(`/admin/totes/${toteId}/shipment-epcis-status`);
    }

    generateToteShipmentEpcis(toteId: number, force = false) {
        return API.post(`/admin/totes/${toteId}/generate-shipment-epcis`, null, {
            params: { force },
        });
    }

    getHealth() {
        return API.get(`${this.base}/health`);
    }

    getPartnerHealth(code: string) {
        return API.get(`${this.base}/partners/${code}/health`);
    }

    getCertificateExpiry(expiresWithinDays?: number) {
        return API.get(`${this.base}/certificates/expiry`, {
            params: expiresWithinDays != null ? { expiresWithinDays } : undefined,
        });
    }

    createPartnerPair(body: IIntegrationPartnerCreateRequest) {
        return API.post(`${this.base}/partners/create-pair`, body);
    }

    updatePartnerGroup(groupCode: string, body: IIntegrationPartnerGroupUpdateRequest) {
        return API.put(`${this.base}/partners/group/${groupCode}`, body);
    }

    markTestPassed(code: string) {
        return API.post(`${this.base}/partners/${code}/mark-test-passed`);
    }

    notifyOnboarding(code: string) {
        return API.post(`${this.base}/partners/${code}/notify-onboarding`);
    }

    uploadCertificate(code: string, file: File) {
        return postMultipart(`${this.base}/partners/${code}/credentials/cert`, certFileForm(file));
    }

    uploadCertificatesPair(
        testCode: string,
        prodCode: string,
        testFile: File | null,
        prodFile: File | null
    ) {
        const form = new FormData();
        form.append('testCode', testCode);
        form.append('prodCode', prodCode);
        if (testFile) {
            form.append('testFile', testFile, testFile.name);
        }
        if (prodFile) {
            form.append('prodFile', prodFile, prodFile.name);
        }
        return postMultipart(`${this.base}/partners/upload-certs-pair`, form);
    }

    /** Partner reference cert from openas2/certs/partners/ or previously uploaded cert. */
    downloadReferenceCertificate(code: string) {
        return API.get(`${this.base}/partners/${code}/reference-certificate`, {
            responseType: 'blob',
        });
    }
}

const integrationsService = new IntegrationsService();
export default integrationsService;
